import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { getDefaultRoute } from './utils/permissions';
import { Login } from './pages/Login';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Documents } from './pages/Documents';
import { DocumentViewer } from './pages/DocumentViewer';
import { AIAssistant } from './pages/AIAssistant';
import { AdminUsers } from './pages/AdminUsers';
import { AdminDocuments } from './pages/AdminDocuments';
import { Suppliers } from './pages/Suppliers';
import { Quotations } from './pages/Quotations';
import { QuotationNew } from './pages/QuotationNew';
import { QuotationDetail } from './pages/QuotationDetail';
import { QuotationCompare } from './pages/QuotationCompare';

// Lazy loaded modules
const Clients = lazy(() => import('./pages/Clients'));
const Projects = lazy(() => import('./pages/Projects'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Budgets = lazy(() => import('./pages/Budgets'));
const Invoices = lazy(() => import('./pages/Invoices'));
const InvoiceInbox = lazy(() => import('./pages/InvoiceInbox'));
const ProjectFinance = lazy(() => import('./pages/ProjectFinance'));
const HR = lazy(() => import('./pages/HR'));
const Calendar = lazy(() => import('./pages/Calendar'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));

const FINANCE_ROLES: AppRole[] = ['ADMIN', 'ACCOUNTING', 'MANAGER', 'ENGINEERING'];

type AppRole = 'ADMIN' | 'ENGINEERING' | 'COMMERCIAL' | 'ACCOUNTING' | 'HR' | 'MANAGER';
const ALL_ROLES: AppRole[] = ['ADMIN', 'ENGINEERING', 'COMMERCIAL', 'ACCOUNTING', 'HR', 'MANAGER'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role as AppRole)) {
    return <Navigate to={getDefaultRoute(user.role)} replace />;
  }

  return <Layout>{children}</Layout>;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  const { isAuthenticated, user } = useAuthStore();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated && user ? (
              <Navigate to={getDefaultRoute(user.role)} replace />
            ) : (
              <Login />
            )
          }
        />

        <Route
          path="/"
          element={
            isAuthenticated && user ? (
              <Navigate to={getDefaultRoute(user.role)} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Comercial */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Clients />
            </ProtectedRoute>
          }
        />

        <Route
          path="/suppliers"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Suppliers />
            </ProtectedRoute>
          }
        />

        <Route
          path="/quotations"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Quotations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotations/new"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <QuotationNew />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotations/:id"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <QuotationDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotations/:id/compare"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <QuotationCompare />
            </ProtectedRoute>
          }
        />

        <Route
          path="/purchase-orders"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <PurchaseOrders />
            </ProtectedRoute>
          }
        />

        <Route
          path="/budgets"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Budgets />
            </ProtectedRoute>
          }
        />

        {/* Operaciones */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Projects />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Inventory />
            </ProtectedRoute>
          }
        />

        {/* Finanzas */}
        <Route
          path="/invoices"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Invoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoice-inbox"
          element={
            <ProtectedRoute allowedRoles={FINANCE_ROLES}>
              <InvoiceInbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-finance"
          element={
            <ProtectedRoute allowedRoles={FINANCE_ROLES}>
              <ProjectFinance />
            </ProtectedRoute>
          }
        />

        {/* RRHH */}
        <Route
          path="/hr"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'HR', 'MANAGER']}>
              <HR />
            </ProtectedRoute>
          }
        />

        {/* Herramientas */}
        <Route
          path="/calendar"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Calendar />
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <Documents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents/:id"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <DocumentViewer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/assistant"
          element={
            <ProtectedRoute allowedRoles={ALL_ROLES}>
              <AIAssistant />
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/documents"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminDocuments />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
